import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import logoCrcUrl from '../assets/logoCrc.png';
import { beautifyAskEvansContent } from '../components/SpacerLog/SpacerLogMarkdown';
import {
  formatAskEvansAnswerMarkdown,
} from './askEvansMarkdownFormat';

/**
 * Colors aligned with Spacer Log HTML brief template
 * (SpacerLogDashboard.css --sl-* + Asset IQ header accents).
 */
const COLORS = {
  ink: rgb(0.2, 0.2, 0.2),
  muted: rgb(0.4, 0.4, 0.4),
  mutedDim: rgb(0.6, 0.6, 0.6),
  accent: rgb(0, 0.659, 0.557),
  red: rgb(0.769, 0.118, 0.118),
  ruleSoft: rgb(0.851, 0.882, 0.906),
  panel: rgb(0.945, 0.949, 0.953),
  tableHead: rgb(0.91, 0.93, 0.94),
  tableBorder: rgb(0.82, 0.87, 0.9),
  codeBg: rgb(0.93, 0.95, 0.96),
};

const PAGE = { width: 595.28, height: 841.89 };
const MARGIN = { left: 54, right: 54, top: 48, bottom: 54 };
const CONTENT_WIDTH = PAGE.width - MARGIN.left - MARGIN.right;

export const sanitizePdfText = (input) =>
  String(input ?? '')
    .normalize('NFKC')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014\u2212]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u2022\u25CF\u25E6\u00B7]/g, '-')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u2190-\u21FF]/g, '->')
    .replace(/[^\t\n\r\x20-\x7E\xA0-\xFF]/g, '');

const stripInlineMd = (text) =>
  sanitizePdfText(
    String(text || '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
  );

const isTableSep = (line) =>
  /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(String(line || '').trim());

const splitRow = (line) => {
  let s = String(line || '').trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => stripInlineMd(c.trim()));
};

export { renumberOrderedListsInMarkdown } from './askEvansMarkdownFormat';

export const parseMarkdownBlocks = (markdown) => {
  const raw = String(markdown || '').replace(/\r\n/g, '\n').trim();
  if (!raw) return [];

  const lines = raw.split('\n');
  const blocks = [];
  let listBuf = [];
  let listOrdered = false;
  let codeBuf = null;

  const flushList = () => {
    if (!listBuf.length) return;
    blocks.push({ type: listOrdered ? 'ol' : 'ul', items: listBuf.slice() });
    listBuf = [];
  };

  const flushCode = () => {
    if (codeBuf == null) return;
    blocks.push({ type: 'code', text: codeBuf.join('\n') });
    codeBuf = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const trimmed = line.trim();

    if (codeBuf != null) {
      if (trimmed.startsWith('```')) {
        flushCode();
      } else {
        codeBuf.push(line);
      }
      continue;
    }

    if (trimmed.startsWith('```')) {
      flushList();
      codeBuf = [];
      continue;
    }

    if (trimmed.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushList();
      const header = splitRow(trimmed);
      i += 1;
      const rows = [];
      while (
        i + 1 < lines.length
        && lines[i + 1].trim().includes('|')
        && !isTableSep(lines[i + 1])
      ) {
        i += 1;
        rows.push(splitRow(lines[i].trim()));
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    if (!trimmed) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j += 1;
      const nextTrimmed = j < lines.length ? lines[j].trim() : '';
      const continuesList =
        listBuf.length > 0
        && (nextTrimmed.match(/^\d+\.\s+/) || nextTrimmed.match(/^[-*+]\s+/));
      if (!continuesList) flushList();
      continue;
    }

    const ul = trimmed.match(/^[-*+]\s+(.+)$/);
    const ol = trimmed.match(/^\d+\.\s+(.+)$/);
    if (ul) {
      if (listOrdered && listBuf.length) {
        listBuf[listBuf.length - 1] = `${listBuf[listBuf.length - 1]}\n• ${ul[1]}`;
        continue;
      }
      if (listBuf.length && listOrdered) flushList();
      listOrdered = false;
      listBuf.push(ul[1]);
      continue;
    }
    if (ol) {
      if (listBuf.length && !listOrdered) flushList();
      listOrdered = true;
      listBuf.push(ol[1]);
      continue;
    }

    flushList();

    if (trimmed.startsWith('#### ')) {
      blocks.push({ type: 'h4', text: trimmed.slice(5) });
      continue;
    }
    if (trimmed.startsWith('### ')) {
      blocks.push({ type: 'h3', text: trimmed.slice(4) });
      continue;
    }
    if (trimmed.startsWith('## ')) {
      blocks.push({ type: 'h2', text: trimmed.slice(3) });
      continue;
    }
    if (trimmed.startsWith('# ')) {
      blocks.push({ type: 'h1', text: trimmed.slice(2) });
      continue;
    }
    if (trimmed.startsWith('> ')) {
      blocks.push({ type: 'quote', text: trimmed.slice(2) });
      continue;
    }
    if (trimmed === '---' || trimmed === '***') {
      blocks.push({ type: 'hr' });
      continue;
    }

    blocks.push({ type: 'p', text: trimmed });
  }

  flushList();
  flushCode();
  return blocks;
};

const wrapText = (text, font, size, maxWidth) => {
  const words = sanitizePdfText(stripInlineMd(text)).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      if (font.widthOfTextAtSize(word, size) <= maxWidth) {
        current = word;
      } else {
        let rest = word;
        while (rest.length) {
          let cut = 1;
          while (
            cut <= rest.length
            && font.widthOfTextAtSize(rest.slice(0, cut), size) <= maxWidth
          ) {
            cut += 1;
          }
          cut = Math.max(1, cut - 1);
          lines.push(rest.slice(0, cut));
          rest = rest.slice(cut);
        }
        current = '';
      }
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [''];
};

/** Split line into plain / bold / code runs (GFM inline subset). */
const tokenizeInline = (text) => {
  const src = String(text || '');
  const segments = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let m;
  while ((m = re.exec(src)) != null) {
    if (m.index > last) {
      segments.push({ kind: 'text', value: src.slice(last, m.index) });
    }
    const token = m[0];
    if (token.startsWith('**')) {
      segments.push({ kind: 'bold', value: token.slice(2, -2) });
    } else if (token.startsWith('`')) {
      segments.push({ kind: 'code', value: token.slice(1, -1) });
    } else {
      segments.push({ kind: 'italic', value: token.slice(1, -1) });
    }
    last = m.index + token.length;
  }
  if (last < src.length) segments.push({ kind: 'text', value: src.slice(last) });
  if (!segments.length) segments.push({ kind: 'text', value: src });
  return segments;
};

const formatBriefDate = (date = new Date()) => {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
};

const safeDrawText = (page, text, opts) => {
  const clean = sanitizePdfText(text);
  if (!clean) return;
  try {
    page.drawText(clean, opts);
  } catch {
    const ascii = clean.replace(/[^\x20-\x7E]/g, '');
    if (ascii) page.drawText(ascii, opts);
  }
};

/**
 * Build Ask Evans PDF matching Asset IQ header + Spacer Log HTML body colours.
 * @returns {Promise<Blob>}
 */
export const buildAskEvansPdfReport = async ({
  markdown,
  title = 'Ask Evans Report',
  eyebrow = 'SPACER LOG - ASK EVANS REPORT',
  subtitle = 'Prepared for operations - CONFIDENTIAL',
  question,
} = {}) => {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const fontOblique = await pdf.embedFont(StandardFonts.HelveticaOblique);

  let logoImage = null;
  try {
    const logoRes = await fetch(logoCrcUrl);
    if (logoRes.ok) {
      const logoBytes = await logoRes.arrayBuffer();
      logoImage = await pdf.embedPng(logoBytes);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Ask Evans PDF: logo unavailable', err);
  }

  const prepared = formatAskEvansAnswerMarkdown(beautifyAskEvansContent(String(markdown || '')));
  const blocks = parseMarkdownBlocks(prepared);
  const pageBlocks = [];

  if (question && String(question).trim()) {
    pageBlocks.push({ type: 'quote', text: stripInlineMd(question) });
  }
  pageBlocks.push(...blocks);

  let page = pdf.addPage([PAGE.width, PAGE.height]);
  let y = PAGE.height - MARGIN.top;

  const drawHeader = (targetPage, { skipTitle = false } = {}) => {
    const headerY = PAGE.height - MARGIN.top;
    const eye = sanitizePdfText(String(eyebrow || '').toUpperCase());

    safeDrawText(targetPage, eye, {
      x: MARGIN.left,
      y: headerY - 4,
      size: 8,
      font,
      color: COLORS.mutedDim,
    });

    if (logoImage) {
      const maxW = 120;
      const maxH = 20;
      const scale = Math.min(maxW / logoImage.width, maxH / logoImage.height);
      const w = logoImage.width * scale;
      const h = logoImage.height * scale;
      targetPage.drawImage(logoImage, {
        x: PAGE.width - MARGIN.right - w,
        y: headerY - h + 2,
        width: w,
        height: h,
      });
    } else {
      const brandSize = 11;
      const crcW = fontBold.widthOfTextAtSize('CRC', brandSize);
      const evansW = font.widthOfTextAtSize('EVANS', brandSize);
      safeDrawText(targetPage, 'EVANS', {
        x: PAGE.width - MARGIN.right - evansW,
        y: headerY - 2,
        size: brandSize,
        font,
        color: COLORS.muted,
      });
      safeDrawText(targetPage, 'CRC', {
        x: PAGE.width - MARGIN.right - evansW - 3 - crcW,
        y: headerY - 2,
        size: brandSize,
        font: fontBold,
        color: COLORS.red,
      });
    }

    const ruleY = headerY - 30;
    targetPage.drawLine({
      start: { x: MARGIN.left, y: ruleY },
      end: { x: PAGE.width - MARGIN.right, y: ruleY },
      thickness: 1.5,
      color: COLORS.red,
    });

    if (skipTitle) return ruleY - 16;

    let cursor = ruleY - 26;
    const dateLine = formatBriefDate();
    const titleText = sanitizePdfText(String(title || 'Ask Evans Report').trim());
    const displayTitle = titleText;
    wrapText(displayTitle, fontBold, 14, CONTENT_WIDTH).forEach((line) => {
      safeDrawText(targetPage, line, {
        x: MARGIN.left,
        y: cursor,
        size: 14,
        font: fontBold,
        color: COLORS.ink,
      });
      cursor -= 18;
    });

    const subBase = sanitizePdfText(
      String(subtitle || 'Prepared for operations · CONFIDENTIAL').trim()
    );
    const sub = subBase.includes(dateLine) ? subBase : `${subBase} · ${dateLine}`;
    wrapText(sub, font, 10, CONTENT_WIDTH).forEach((line) => {
      safeDrawText(targetPage, line, {
        x: MARGIN.left,
        y: cursor,
        size: 10,
        font,
        color: COLORS.muted,
      });
      cursor -= 14;
    });

    return cursor - 10;
  };

  const ensureSpace = (needed) => {
    if (y - needed >= MARGIN.bottom) return;
    page = pdf.addPage([PAGE.width, PAGE.height]);
    drawHeader(page, { skipTitle: true });
    y = PAGE.height - MARGIN.top - 52;
  };

  y = drawHeader(page, { skipTitle: false });

  const pickFont = (kind, bold = false) => {
    if (kind === 'bold' || bold) return fontBold;
    if (kind === 'italic') return fontOblique;
    return font;
  };

  const drawInlineParagraph = (text, { size = 10.5, indent = 0, color = COLORS.ink, lineGap = 4 } = {}) => {
    const maxW = CONTENT_WIDTH - indent;
    const plain = sanitizePdfText(String(text || ''));
    if (!plain.includes('**') && !plain.includes('`') && !plain.includes('*')) {
      wrapText(plain, font, size, maxW).forEach((line) => {
        ensureSpace(size + lineGap + 2);
        safeDrawText(page, line, {
          x: MARGIN.left + indent,
          y,
          size,
          font,
          color,
        });
        y -= size + lineGap;
      });
      return;
    }

    const segments = tokenizeInline(text);
    const tokens = [];
    segments.forEach((seg) => {
      sanitizePdfText(seg.value)
        .split(/\s+/)
        .filter(Boolean)
        .forEach((word) => {
          tokens.push({ word, kind: seg.kind });
        });
    });

    let line = [];
    let lineW = 0;

    const flushLine = () => {
      if (!line.length) return;
      ensureSpace(size + lineGap + 2);
      let x = MARGIN.left + indent;
      line.forEach((tok, idx) => {
        const useFont = pickFont(tok.kind, tok.kind === 'code');
        const drawSize = tok.kind === 'code' ? size - 0.5 : size;
        const chunk = idx === 0 ? tok.word : ` ${tok.word}`;
        safeDrawText(page, chunk, {
          x,
          y,
          size: drawSize,
          font: useFont,
          color: tok.kind === 'code' ? COLORS.muted : color,
        });
        x += useFont.widthOfTextAtSize(chunk, drawSize);
      });
      y -= size + lineGap;
      line = [];
      lineW = 0;
    };

    tokens.forEach((tok) => {
      const useFont = pickFont(tok.kind, tok.kind === 'code');
      const drawSize = tok.kind === 'code' ? size - 0.5 : size;
      const addW = useFont.widthOfTextAtSize(
        line.length ? ` ${tok.word}` : tok.word,
        drawSize
      );
      if (lineW + addW > maxW && line.length) {
        flushLine();
      }
      line.push(tok);
      lineW += useFont.widthOfTextAtSize(
        line.length === 1 ? tok.word : ` ${tok.word}`,
        drawSize
      );
    });
    flushLine();
  };

  const drawHeading = (text, level) => {
    const isQuestion = /^Question\s+\d+/i.test(stripInlineMd(text));
    const isAnswer = /^Answer(\s+\d+)?$/i.test(stripInlineMd(text));
    const sizes = { h1: 13, h2: 12, h3: 11, h4: 10.5 };
    const size = sizes[level] || 11;
    y -= level === 'h1' ? 10 : 8;
    ensureSpace(size + 12);
    if (isQuestion || isAnswer) {
      page.drawRectangle({
        x: MARGIN.left,
        y: y - 2,
        width: 3,
        height: size + 4,
        color: isQuestion ? COLORS.red : COLORS.accent,
      });
      drawInlineParagraph(text, { size, indent: 10, color: COLORS.ink, lineGap: 3 });
    } else {
      drawInlineParagraph(text, { size, color: COLORS.ink, lineGap: 3 });
    }
    if (level === 'h1' || level === 'h2') {
      ensureSpace(6);
      page.drawLine({
        start: { x: MARGIN.left, y: y + 2 },
        end: { x: MARGIN.left + (level === 'h1' ? 80 : 56), y: y + 2 },
        thickness: 1,
        color: COLORS.accent,
      });
      y -= 4;
    } else {
      y -= 2;
    }
  };

  const drawTable = (header, rows) => {
    const cols = Math.max(header.length, ...rows.map((r) => r.length), 1);
    const colWidth = CONTENT_WIDTH / cols;
    const cellPad = 4;
    const rowH = 16;
    const headH = 18;
    const fontSize = 9;

    ensureSpace(headH + 4);
    page.drawRectangle({
      x: MARGIN.left,
      y: y - headH + 4,
      width: CONTENT_WIDTH,
      height: headH,
      color: COLORS.tableHead,
      borderColor: COLORS.tableBorder,
      borderWidth: 0.5,
    });
    for (let c = 0; c < cols; c += 1) {
      const label = stripInlineMd(header[c] || '');
      const cellText = wrapText(label, fontBold, fontSize, colWidth - cellPad * 2)[0] || '';
      safeDrawText(page, cellText, {
        x: MARGIN.left + c * colWidth + cellPad,
        y: y - headH + 9,
        size: fontSize,
        font: fontBold,
        color: COLORS.ink,
      });
    }
    y -= headH + 2;

    rows.forEach((row, ri) => {
      ensureSpace(rowH + 2);
      if (ri % 2 === 1) {
        page.drawRectangle({
          x: MARGIN.left,
          y: y - rowH + 4,
          width: CONTENT_WIDTH,
          height: rowH,
          color: COLORS.panel,
        });
      }
      for (let c = 0; c < cols; c += 1) {
        const cellText =
          wrapText(stripInlineMd(row[c] || ''), font, fontSize, colWidth - cellPad * 2)[0] || '';
        safeDrawText(page, cellText, {
          x: MARGIN.left + c * colWidth + cellPad,
          y: y - rowH + 9,
          size: fontSize,
          font,
          color: COLORS.ink,
        });
      }
      page.drawLine({
        start: { x: MARGIN.left, y: y - rowH + 3 },
        end: { x: PAGE.width - MARGIN.right, y: y - rowH + 3 },
        thickness: 0.4,
        color: COLORS.tableBorder,
      });
      y -= rowH;
    });
    y -= 6;
  };

  pageBlocks.forEach((block) => {
    if (block.type === 'h1') {
      drawHeading(block.text, 'h1');
      return;
    }
    if (block.type === 'h2') {
      drawHeading(block.text, 'h2');
      return;
    }
    if (block.type === 'h3') {
      drawHeading(block.text, 'h3');
      return;
    }
    if (block.type === 'h4') {
      drawHeading(block.text, 'h4');
      return;
    }
    if (block.type === 'hr') {
      y -= 6;
      ensureSpace(10);
      page.drawLine({
        start: { x: MARGIN.left, y: y },
        end: { x: PAGE.width - MARGIN.right, y: y },
        thickness: 0.75,
        color: COLORS.ruleSoft,
      });
      y -= 10;
      return;
    }
    if (block.type === 'quote') {
      y -= 4;
      const qLines = wrapText(block.text, font, 10, CONTENT_WIDTH - 20);
      const boxH = qLines.length * 13 + 8;
      ensureSpace(boxH + 4);
      page.drawRectangle({
        x: MARGIN.left,
        y: y - boxH + 6,
        width: CONTENT_WIDTH,
        height: boxH,
        color: COLORS.panel,
      });
      page.drawRectangle({
        x: MARGIN.left,
        y: y - boxH + 6,
        width: 3,
        height: boxH,
        color: COLORS.accent,
      });
      qLines.forEach((line, li) => {
        safeDrawText(page, line, {
          x: MARGIN.left + 12,
          y: y - li * 13,
          size: 10,
          font,
          color: COLORS.ink,
        });
      });
      y -= boxH + 4;
      return;
    }
    if (block.type === 'code') {
      y -= 4;
      const codeLines = String(block.text || '').split('\n');
      const lineH = 11;
      let totalLines = 0;
      const wrapped = [];
      codeLines.forEach((line) => {
        const parts = wrapText(line, font, 9, CONTENT_WIDTH - 16);
        wrapped.push(...parts);
        totalLines += parts.length;
      });
      const boxH = totalLines * lineH + 10;
      ensureSpace(boxH + 4);
      page.drawRectangle({
        x: MARGIN.left,
        y: y - boxH + 6,
        width: CONTENT_WIDTH,
        height: boxH,
        color: COLORS.codeBg,
        borderColor: COLORS.tableBorder,
        borderWidth: 0.5,
      });
      wrapped.forEach((line, li) => {
        safeDrawText(page, line, {
          x: MARGIN.left + 8,
          y: y - li * lineH,
          size: 9,
          font,
          color: COLORS.muted,
        });
      });
      y -= boxH + 4;
      return;
    }
    if (block.type === 'table') {
      drawTable(block.header || [], block.rows || []);
      return;
    }
    if (block.type === 'ul' || block.type === 'ol') {
      y -= 2;
      block.items.forEach((item, i) => {
        const rawItem = String(item || '');
        const segments = rawItem.split('\n');
        const head = segments[0] || '';
        const subs = segments.slice(1).map((s) => s.replace(/^•\s*/, '').trim()).filter(Boolean);
        const bullet = block.type === 'ol' ? `${i + 1}.` : '-';
        ensureSpace(16);
        safeDrawText(page, bullet, {
          x: MARGIN.left,
          y,
          size: 10,
          font: block.type === 'ol' ? fontBold : font,
          color: COLORS.accent,
        });
        const prefixW = fontBold.widthOfTextAtSize(`${bullet}  `, 10);
        const beforeY = y;
        drawInlineParagraph(head, {
          size: 10,
          indent: prefixW,
          lineGap: 3,
        });
        if (y === beforeY) y -= 13;
        subs.forEach((sub) => {
          drawInlineParagraph(sub, {
            size: 9.5,
            indent: prefixW + 10,
            lineGap: 2,
            color: COLORS.muted,
          });
        });
        y -= 4;
      });
      y -= 4;
      return;
    }
    y -= 2;
    drawInlineParagraph(block.text, { size: 10.5, lineGap: 4 });
    y -= 4;
  });

  const bytes = await pdf.save();
  const copy = new Uint8Array(bytes);
  return new Blob([copy.buffer], { type: 'application/pdf' });
};
